import React, {
	type ReactNode,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type MouseEvent,
	type RefObject,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { graphql, useFragment } from 'react-relay';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { main_issueNavigator_CardList_fragment$key as CardListType } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_CardList_fragment.graphql';
import type { main_issueNavigator_CardWithSelectedIssue_fieldSets$key as FieldSetsFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_CardWithSelectedIssue_fieldSets.graphql';
import type { main_issueNavigator_CardWithSelectedIssue_issue$key as CardWithSelectedIssueFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_CardWithSelectedIssue_issue.graphql';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { getMaxIssuesPerPage } from '../../../../common/constants.tsx';
import { isNonNullish, isNormalClick } from '../../../../common/utils/index.tsx';
import { markOnce, marks } from '../../../../common/utils/performance-analytics.tsx';
import {
	useFocusedIssueIndex,
	useSelectedIssueStateOldActions,
} from '../../../../controllers/selected-issue-state-old/index.tsx';
import { useSelectedIssueIndex } from '../../../../controllers/selected-issue/facade.tsx';
import {
	useIsSelectedIssue,
	useSelectedIssueActions,
} from '../../../../controllers/selected-issue/hooks.tsx';
import CardEmptyList from './card-empty-list/index.tsx';
import CardSkeleton from './card-skeleton/index.tsx';
import EmptyCard from './card/empty-card/index.tsx';
import Card, { type Props as CardProps } from './card/main.tsx';
import KeyboardShortcuts from './keyboard-shortcuts/index.tsx';
import messages from './messages.tsx';

export type CardListProps = {
	fragment: CardListType;
	loading: boolean;
	onNavigateToIssue: (issueKey: string) => void;
	listRef?: RefObject<HTMLUListElement>;
};

type CardWithAnalyticsProps = {
	issue: CardProps['issue'] | undefined;
	fieldSets: CardProps['fieldSets'];
	index?: number;
	isFocused: boolean;
	isSelected: boolean;
	onFocus: () => void;
	onBlur: () => void;
};

const BLANK_CARD_ANALYTICS_ID = 'blankIssueCard';
const ISSUE_CARD_ANALYTICS_ID = 'issueCard';

type CardWithSelectedIssueProps = {
	issue: CardWithSelectedIssueFragment;
	fieldSets: FieldSetsFragment | null;
	isFocused: boolean;
	onFocus: (issueKey: string) => void;
	onBlur: () => void;
};
const CardWithSelectedIssue = ({
	issue,
	fieldSets,
	isFocused,
	onFocus,
	onBlur,
}: CardWithSelectedIssueProps) => {
	const issueData = useFragment<CardWithSelectedIssueFragment>(
		graphql`
			fragment main_issueNavigator_CardWithSelectedIssue_issue on JiraIssue {
				key
				...main_issueNavigator_Card_issue
			}
		`,
		issue,
	);
	const fieldSetsData = useFragment<FieldSetsFragment>(
		graphql`
			fragment main_issueNavigator_CardWithSelectedIssue_fieldSets on JiraIssueFieldSetConnection {
				...main_issueNavigator_Card_fieldSets
			}
		`,
		fieldSets,
	);
	const { key } = issueData;

	const isSelected = useIsSelectedIssue(key);
	const { setSelectedIssueByKey } = useSelectedIssueActions();

	const onCardFocus = useCallback(() => {
		onFocus(key);
		setSelectedIssueByKey(key, { shouldDebounce: true });
	}, [key, onFocus, setSelectedIssueByKey]);

	return (
		<CardWithAnalytics
			issue={issueData}
			fieldSets={fieldSetsData ?? null}
			isFocused={isFocused}
			isSelected={isSelected}
			onFocus={onCardFocus}
			onBlur={onBlur}
		/>
	);
};

type CardWithSelectedIssueStateOldProps = {
	issue: CardProps['issue'] | undefined;
	fieldSets: CardProps['fieldSets'];
	index: number;
};

const CardWithSelectedIssueStateOld = ({
	index,
	issue,
	fieldSets,
}: CardWithSelectedIssueStateOldProps) => {
	const selectedIssueIndex = useSelectedIssueIndex();
	const { setSelectedIssueByIndex } = useSelectedIssueStateOldActions();
	const [focusedIssueIndex, { setFocusedIssueByIndex, resetFocusedIssue }] = useFocusedIssueIndex();
	const isSelected = selectedIssueIndex === index;
	const isFocused = focusedIssueIndex === index;

	const onCardFocus = useCallback(() => {
		setFocusedIssueByIndex(index);
		setSelectedIssueByIndex(index, true);
	}, [index, setFocusedIssueByIndex, setSelectedIssueByIndex]);

	return (
		<CardWithAnalytics
			issue={issue}
			fieldSets={fieldSets}
			index={index}
			isFocused={isFocused}
			isSelected={isSelected}
			onFocus={onCardFocus}
			onBlur={resetFocusedIssue}
		/>
	);
};

const CardWithAnalytics = ({
	index,
	isFocused,
	isSelected,
	issue,
	fieldSets,
	onFocus,
	onBlur,
}: CardWithAnalyticsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cardRef = useRef<HTMLAnchorElement>(null);

	useLayoutEffect(() => {
		if (isSelected) {
			// Scroll to the card whenever it is selected and focus on it
			cardRef.current?.scrollIntoView && cardRef.current?.scrollIntoView({ block: 'nearest' });
			isFocused && cardRef.current?.focus();
		}
	}, [isSelected, isFocused]);

	const onClickWithAnalytics = useCallback(
		(event: MouseEvent) => {
			const id = issue ? ISSUE_CARD_ANALYTICS_ID : BLANK_CARD_ANALYTICS_ID;
			const attributes = {
				keyboardShortcut: false,
				// TODO Remove attribute when cleaning up jira_spreadsheet_component_m1
				issueIndex: index !== undefined ? index + 1 : undefined,
			};

			if (isNormalClick(event)) {
				event.preventDefault();
				const analyticsEvent = createAnalyticsEvent({
					action: 'selected',
					actionSubject: 'card',
				});
				fireUIAnalytics(analyticsEvent, id, attributes);
			} else {
				const analyticsEvent = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'card',
				});
				fireUIAnalytics(analyticsEvent, id, attributes);
			}
		},
		[createAnalyticsEvent, issue, index],
	);

	const onContextMenuHandler = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'contextMenu clicked',
			actionSubject: 'card',
		});

		fireUIAnalytics(analyticsEvent, ISSUE_CARD_ANALYTICS_ID, {
			keyboardShortcut: false,
			// TODO Remove attribute when cleaning up jira_spreadsheet_component_m1
			issueIndex: index !== undefined ? index + 1 : undefined,
		});
	}, [createAnalyticsEvent, index]);

	return issue == null ? (
		<EmptyCard
			ref={cardRef}
			isSelected={isSelected}
			onClick={onClickWithAnalytics}
			onFocus={onFocus}
		/>
	) : (
		<Card
			ref={cardRef}
			issue={issue}
			fieldSets={fieldSets}
			onClick={onClickWithAnalytics}
			onFocus={onFocus}
			onBlur={onBlur}
			isSelected={isSelected}
			onContextMenu={onContextMenuHandler}
		/>
	);
};

// Exported for DI in tests
export const useFocusedIssueKey = () => useState<IssueKey | null>(null);

const CardList = ({ fragment, loading, onNavigateToIssue, listRef }: CardListProps) => {
	markOnce(marks.ISSUE_RESULTS_DETAIL_VIEW_CARD_LIST_START);

	// Analytics
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Main
	let focusedIssueIndex = null;
	let setFocusedIssueByIndex = noop;
	if (!expVal('jira_spreadsheet_component_m1', 'isInfiniteScrollingEnabled', false)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const result = useFocusedIssueIndex();
		focusedIssueIndex = result[0];
		setFocusedIssueByIndex = result[1].setFocusedIssueByIndex;
	}
	const [focusedIssueKey, setFocusedIssueKey] = useFocusedIssueKey();

	const maxIssuesPerPage = getMaxIssuesPerPage();

	const { formatMessage } = useIntl();

	useLayoutEffect(() => {
		markOnce(marks.ISSUE_RESULTS_DETAIL_VIEW_CARD_LIST_END);
	}, []);

	const fragmentData = useFragment<CardListType>(
		graphql`
			fragment main_issueNavigator_CardList_fragment on JiraIssueConnection
			@argumentDefinitions(
				isJscInfiniteScrollEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-jsc-infinite-scroll-enabled.relayprovider"
				}
				isJiraItrM4Exp3Enabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-jira-itr-m4-exp-3-enabled.relayprovider"
				}
			) {
				edges {
					node {
						key
						...main_issueNavigator_Card_issue
						...main_issueNavigator_CardWithSelectedIssue_issue
						...keyboardShortcuts_issueNavigator @include(if: $isJiraItrM4Exp3Enabled)
					}
					fieldSets(first: 500)
						@optIn(to: "JiraSpreadsheetComponent-M1")
						@include(if: $isJscInfiniteScrollEnabled) {
						...main_issueNavigator_Card_fieldSets
						...main_issueNavigator_CardWithSelectedIssue_fieldSets
					}
				}
			}
		`,
		fragment,
	);

	const onIssueChangeEvent = useCallback(
		// TODO Remove issueIndex argument when cleaning up jira_spreadsheet_component_m1
		(issueKey: IssueKey, issueIndex?: number | null) => {
			if (expVal('jira_spreadsheet_component_m1', 'isInfiniteScrollingEnabled', false)) {
				if (issueKey) {
					setFocusedIssueKey(issueKey);
				}
			} else if (issueIndex !== null) {
				setFocusedIssueByIndex(issueIndex);
			}
			const id = issueKey ? ISSUE_CARD_ANALYTICS_ID : BLANK_CARD_ANALYTICS_ID;

			const analyticsEvent = createAnalyticsEvent({
				action: 'selected',
				actionSubject: 'card',
			});

			fireUIAnalytics(analyticsEvent, id, {
				keyboardShortcut: true,
				issueIndex: issueIndex == null ? null : issueIndex + 1,
			});
		},
		[createAnalyticsEvent, setFocusedIssueByIndex, setFocusedIssueKey],
	);

	let cards;
	if (expVal('jira_spreadsheet_component_m1', 'isInfiniteScrollingEnabled', false)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		cards = useMemo(
			() =>
				fragmentData?.edges?.map(
					(edge) =>
						edge?.node && (
							<CardWithSelectedIssue
								key={edge.node.key}
								isFocused={edge.node.key === focusedIssueKey}
								issue={edge.node}
								fieldSets={edge.fieldSets ?? null}
								onFocus={setFocusedIssueKey}
								onBlur={() => setFocusedIssueKey(null)}
							/>
						),
				) ?? [],
			[fragmentData?.edges, focusedIssueKey, setFocusedIssueKey],
		);
	} else {
		cards =
			fragmentData?.edges
				?.filter(isNonNullish)
				.map((edge, index: number) => (
					<CardWithSelectedIssueStateOld
						key={index}
						index={index}
						issue={edge.node ?? undefined}
						fieldSets={edge.fieldSets ?? null}
					/>
				)) ?? [];
	}

	const focusedIssue = useMemo(() => {
		if (expVal('jira-itr-m4-exp-3', 'isEnabled', false)) {
			return fragmentData?.edges?.find((edge) => edge?.node?.key === focusedIssueKey)?.node ?? null;
		}
		return null;
	}, [focusedIssueKey, fragmentData?.edges]);

	if (loading) {
		return (
			<StyledList aria-label={formatMessage(messages.issues)}>
				{[...Array(maxIssuesPerPage)].map<ReactNode>((__, index) => (
					<CardSkeleton key={index} issueIndex={index} />
				))}
			</StyledList>
		);
	}

	if (cards.length === 0) {
		return <CardEmptyList />;
	}

	return (
		<>
			<KeyboardShortcuts
				focusedIssueIndex={focusedIssueIndex}
				focusedIssueKey={focusedIssueKey}
				onIssueChangeEvent={onIssueChangeEvent}
				onNavigateToIssue={onNavigateToIssue}
				disableListControls={loading}
				focusedIssue={focusedIssue}
			/>
			<StyledList
				ref={
					expVal('jira_spreadsheet_component_m1', 'isInfiniteScrollingEnabled', false)
						? listRef
						: undefined
				}
				aria-label={formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.issuesIssueTermRefresh
						: messages.issues,
				)}
			>
				{cards}
			</StyledList>
		</>
	);
};

export default CardList;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StyledList = styled.ul({
	listStyle: 'none',
	paddingLeft: '0px',
});
